import { StateCreator } from 'zustand';
import { AppState, SettingsState, CountryData, AppSettings } from '../types';
import { saveToLocalStorage } from '../utils';

// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// 💉 CHIRURGIE-FIX: KLARE TRENNUNG VON TEMPLATE UND MANDANT
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

/** Normalisiert Roh-Land (DE/AT/CH/IT, inkl. Südtirol/Italien-Texte). */
export const normalizeLandCode = (raw: unknown): 'DE' | 'AT' | 'CH' | 'IT' => {
    const s = String(raw || '')
        .toUpperCase()
        .trim()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, ''); // Ö→O, ü→u
    if (!s) return 'DE';
    if (s === 'DE' || s.includes('DEUTSCHLAND') || s.includes('GERMANY')) return 'DE';
    if (s === 'AT' || s.includes('OSTERREICH') || s.includes('AUSTRIA')) return 'AT';
    if (s === 'CH' || s.includes('SCHWEIZ') || s.includes('SWITZERLAND')) return 'CH';
    if (
        s === 'IT' ||
        s.includes('ITALIEN') ||
        s.includes('ITALY') ||
        s.includes('SUEDTIROL') ||
        s.includes('SUDTIROL') ||
        s.includes('SOUTH TYROL') ||
        s.includes('ALTO ADIGE')
    ) {
        return 'IT';
    }
    return 'DE';
};

const defaultMwstForLand = (land: 'DE' | 'AT' | 'CH' | 'IT'): number => {
    if (land === 'CH') return 8.1;
    if (land === 'AT') return 20;
    if (land === 'IT') return 22;
    return 19;
};

const defaultLnkForLand = (land: 'DE' | 'AT' | 'CH' | 'IT'): number => {
    if (land === 'CH') return 17.0;
    if (land === 'AT') return 52.0;
    if (land === 'IT') return 30;
    return 22.5;
};

/**
 * Aktives Länderprofil (MwSt, Lohnnebenkosten, Mindestlohn, Währung).
 * @param preferSettingsLand true = selectedCountryId schlägt clientData.land
 *   (nötig beim Admin-Landwechsel, bevor clientData nachzieht).
 */
export const resolveActiveCountry = (
    settings: any,
    clientData?: any,
    opts?: { preferSettingsLand?: boolean }
): CountryData => {
    const rawLand = opts?.preferSettingsLand
        ? (settings?.selectedCountryId || clientData?.land || clientData?.Land || 'DE')
        : (clientData?.Land ||
            clientData?.land ||
            clientData?.clientData?.Land ||
            clientData?.clientData?.land ||
            settings?.selectedCountryId ||
            'DE');

    const activeLand = normalizeLandCode(rawLand);

    const base = settings?.countries?.[activeLand] || settings?.countries?.DE || {};
    const overrides = settings?.countryOverrides?.[activeLand] || {};

    const mwst =
        overrides.MwSt_Satz ?? base.MwSt_Satz ?? defaultMwstForLand(activeLand);
    const currencyCode = overrides.currency ?? base.currency ?? (activeLand === 'CH' ? 'CHF' : 'EUR');
    const rawLnk =
        overrides.Lohnnebenkosten ??
        base.Lohnnebenkosten ??
        defaultLnkForLand(activeLand);
    const lnk =
        activeLand === 'AT' && Number(rawLnk) === 30.5
            ? 52.0
            : activeLand === 'CH' && Number(rawLnk) === 15
                ? 17.0
                : rawLnk;

    const rawLnkMini =
        overrides.Lohnnebenkosten_Minijob ??
        base.Lohnnebenkosten_Minijob ??
        (activeLand === 'DE' ? 30 : activeLand === 'AT' ? 16.4 : lnk);
    const lnkMini =
        activeLand === 'AT' && (Number(rawLnkMini) === 30.5 || Number(rawLnkMini) === 52.0)
            ? 16.4
            : activeLand === 'CH' && Number(rawLnkMini) === 15
                ? 17.0
                : rawLnkMini;

    const minijobGrenzeResolved =
        activeLand === 'AT'
            ? (Number(overrides.Minijob_Lohn_Grenze) > 0
                ? Number(overrides.Minijob_Lohn_Grenze)
                : Number(base.Minijob_Lohn_Grenze) > 0
                    ? Number(base.Minijob_Lohn_Grenze)
                    : 518.44)
            : (overrides.Minijob_Lohn_Grenze ?? base.Minijob_Lohn_Grenze ?? 0);

    const itBzExtras =
        activeLand === 'IT'
            ? {
                  ItBz_Faktor_Azubi:
                      overrides.ItBz_Faktor_Azubi ?? base.ItBz_Faktor_Azubi ?? 1.42,
                  ItBz_Faktor_Teilzeit:
                      overrides.ItBz_Faktor_Teilzeit ?? base.ItBz_Faktor_Teilzeit ?? 1.65,
                  ItBz_Faktor_Vollzeit:
                      overrides.ItBz_Faktor_Vollzeit ?? base.ItBz_Faktor_Vollzeit ?? 1.80,
                  ItBz_Vollzeit_Stundenschwelle:
                      overrides.ItBz_Vollzeit_Stundenschwelle ??
                      base.ItBz_Vollzeit_Stundenschwelle ??
                      30,
              }
            : {};

    const atExtras =
        activeLand === 'AT'
            ? {
                  At_Lohnnebenkosten_Fachkraft:
                      overrides.At_Lohnnebenkosten_Fachkraft ??
                      base.At_Lohnnebenkosten_Fachkraft ??
                      52.0,
                  At_Lohnnebenkosten_Azubi:
                      overrides.At_Lohnnebenkosten_Azubi ??
                      base.At_Lohnnebenkosten_Azubi ??
                      35.0,
              }
            : {};

    const chExtras =
        activeLand === 'CH'
            ? {
                  vatThreshold:
                      overrides.vatThreshold ??
                      base.vatThreshold ??
                      100000,
                  isVatExempt:
                      overrides.isVatExempt !== undefined
                          ? Boolean(overrides.isVatExempt)
                          : base.isVatExempt !== undefined
                              ? Boolean(base.isVatExempt)
                              : true,
                  voluntaryOption:
                      overrides.voluntaryOption !== undefined
                          ? Boolean(overrides.voluntaryOption)
                          : base.voluntaryOption !== undefined
                              ? Boolean(base.voluntaryOption)
                              : false,
              }
            : {};

    return {
        ...base,
        ...overrides,
        ...itBzExtras,
        ...atExtras,
        ...chExtras,
        currency: currencyCode,
        MwSt_Satz: mwst,
        Lohnnebenkosten: lnk,
        Lohnnebenkosten_Minijob: lnkMini,
        Minijob_Lohn_Grenze: minijobGrenzeResolved,
    };
};

/**
 * Effektiver MwSt.-Satz des Mandanten unter Berücksichtigung von:
 * 1. PRIO 0: Universeller Hard-Override für ALLE Länder (DE, AT, CH, IT_BZ) bei Mandanten-Umsatzsteuerbefreiung
 * 2. PRIO 1: Schweiz (CH) Art. 10 MWSTG: 0,0 % unterhalb vatThreshold (100'000 CHF), sofern keine voluntaryOption aktiv ist
 */
export const resolveEffectiveClientMwstSatz = (settings: any, clientData?: any): number => {
    const isMandantUstBefreit = Boolean(
        clientData?.umsatzsteuerbefreit === true ||
            clientData?.isUmsatzsteuerbefreit === true ||
            clientData?.isVatExempt === true ||
            clientData?.clientData?.umsatzsteuerbefreit === true ||
            clientData?.clientData?.isUmsatzsteuerbefreit === true ||
            clientData?.clientData?.isVatExempt === true
    );

    // PRIO 0: Universeller Hard-Override für ALLE Länder (DE, AT, CH, IT_BZ)
    if (isMandantUstBefreit) {
        return 0.0;
    }

    const resolved = resolveActiveCountry(settings, clientData);
    const rawLand =
        clientData?.Land ||
        clientData?.land ||
        clientData?.clientData?.Land ||
        clientData?.clientData?.land ||
        settings?.selectedCountryId ||
        'DE';
    const activeLand = normalizeLandCode(rawLand);
    const standardRate =
        resolved.MwSt_Satz !== undefined && resolved.MwSt_Satz !== null
            ? Number(resolved.MwSt_Satz)
            : defaultMwstForLand(activeLand);

    if (activeLand === 'CH') {
        const voluntaryOption = Boolean(resolved.voluntaryOption);
        const settingIsVatExempt = resolved.isVatExempt !== undefined ? Boolean(resolved.isVatExempt) : true;
        const vatThreshold = Number(resolved.vatThreshold) > 0 ? Number(resolved.vatThreshold) : 100000;
        const rawUmsatz = clientData?.bwaData?.umsatz ?? clientData?.clientData?.bwaData?.umsatz ?? 0;
        const umsatz =
            typeof rawUmsatz === 'number'
                ? rawUmsatz
                : parseFloat(String(rawUmsatz).replace(/\./g, '').replace(',', '.')) || 0;

        if (voluntaryOption || settingIsVatExempt === false) return standardRate;
        if (umsatz >= vatThreshold) return standardRate;
        return 0;
    }

    return Number.isFinite(standardRate) ? standardRate : defaultMwstForLand(activeLand);
};

// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// DEFAULTS
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

const countriesData: Record<string, CountryData> = { 
    DE: { name: 'Deutschland', Mindestlohn: 13.90, Minijob_Lohn_Grenze: 602.00, Lohnnebenkosten: 22.5, Lohnnebenkosten_Minijob: 30.0, MwSt_Satz: 19.0, currency: 'EUR' }, 
    AT: {
        name: 'Österreich',
        Mindestlohn: 0,
        Minijob_Lohn_Grenze: 518.44,
        Lohnnebenkosten: 52.0,
        Lohnnebenkosten_Minijob: 16.4,
        MwSt_Satz: 20.0,
        currency: 'EUR',
        At_Lohnnebenkosten_Fachkraft: 52.0,
        At_Lohnnebenkosten_Azubi: 35.0,
    }, 
    CH: {
        name: 'Schweiz',
        Mindestlohn: 0,
        Minijob_Lohn_Grenze: 0,
        Lohnnebenkosten: 17.0,
        Lohnnebenkosten_Minijob: 17.0,
        MwSt_Satz: 8.1,
        currency: 'CHF',
        vatThreshold: 100000,
        isVatExempt: true,
        voluntaryOption: false,
    }, 
    IT: { 
        name: 'Italien', 
        Mindestlohn: 0, 
        Minijob_Lohn_Grenze: 0, 
        Lohnnebenkosten: 30.0, 
        Lohnnebenkosten_Minijob: 30.0, 
        MwSt_Satz: 22.0, 
        currency: 'EUR',
        ItBz_Faktor_Azubi: 1.42,
        ItBz_Faktor_Teilzeit: 1.65,
        ItBz_Faktor_Vollzeit: 1.80,
        ItBz_Vollzeit_Stundenschwelle: 30
    } 
};

// --- WEBDESIGN STAMMDATEN (Synchronisiert mit KalkulationsdatenPage) ---
const defaultWebDesignPackages = {
    // Basic 12 Monate
    basic_mon_12: 85.00,
    basic_gesamt_12: 1020.00,
    basic_zs_12: 8.00,
    
    // Basic 24 Monate
    basic_mon_24: 50.00,
    basic_gesamt_24: 1200.00,
    basic_zs_24: 4.00,

    // Basic Kauf
    basic_kauf: 1950.00,

    // Individuell 12 Monate
    indiv_mon_12: 162.40,
    indiv_gesamt_12: 1950.00,
    indiv_zs_12: 12.50,

    // Individuell 24 Monate
    indiv_mon_24: 89.50,
    indiv_gesamt_24: 2148.00,
    indiv_zs_24: 6.25,

    // Individuell Kauf
    indiv_kauf: 3650.00,

    // SEO
    seo_monatl_12: 16.50,
    seo_monatl_24: 8.25,
    seo_gesamt: 198.00,

    // Bilder
    basicImageUrl: "https://i.imgur.com/LoNDknO.png",
    individualImageUrl: "https://i.imgur.com/Cfg8Kpv.png",
    zusatzImageUrl: "https://i.imgur.com/pzxEKJH.png"
};

export const defaultSettings: SettingsState = { 
    selectedCountryId: 'DE', 
    global: { 
        Warenwirtschaftsaufschlag_VK: 1.3, 
        Warenwirtschaftsaufschlag_DL: 1.05, 
        Investlinie: 5, 
        Gewinnlinie: 22, 
        Zusatzumsatz: 30, 
        Verlust_1: 15, 
        Liquiditaet: 10, 
        Wochensatz: 4.33, 
        Urlaubsausgleich: 20, 
        Auslastungsfaktor: 75, 
        Auslastungsfaktor_Max: 85, 
        Grobkundenaufschlag: 0.4, 
        Grobkundenaufschlag_ab: 42000, 
        Insider_Solo: 74, 
        Insider_Team: 167, 
        Neukunden_Aufschlag: 30,
        basicStandardPreis: 49, 
        Tagessatz: 1600, 
        Stundensatz: 0, 
        KmRate: 0.60,
        geraeteListe: [
            { id: 'dev1', name: 'Laser X1', kostenProzent: 3.5 },
            { id: 'dev2', name: 'IPL-System Y2', kostenProzent: 2.0 },
            { id: 'dev3', name: 'Mikrodermabrasion', kostenProzent: 1.5 },
            { id: 'dev4', name: 'Ultraschall', kostenProzent: 1.0 }
        ]
    }, 
    webdesignPackages: defaultWebDesignPackages as any, 
    countries: countriesData, 
    countryOverrides: {}, 
    country: {},
    materialListe: [
        { id: 'mat1', name: 'Kemon Cramer Color 100ml', gebinde: 100, ekPreis: 7.15 },
        { id: 'mat2', name: 'Kemon Blondierung 800g', gebinde: 800, ekPreis: 41.75 },
        { id: 'mat3', name: 'Farbe allgemein 60ml', gebinde: 60, ekPreis: 9.50 }
    ],
    surchargeListe: [
        { id: 'sur1', description: 'Calligraphy cut Black', price: 15.00 },
        { id: 'sur2', description: 'Calligraphy cut Silver', price: 20.00 },
        { id: 'sur3', description: 'Calligraphy cut Gold', price: 30.00 },
        { id: 'sur4', description: 'Feuchtigkeitsbooster', price: 13.00 }
    ],
    // Preis-Parameter gehören in die Mandanten-Akte (kalkulator) — hier bewusst leer
    priceParameterListe: [],
    downloadItems: [],
    downloadCategories: [],
    tutorialVideos: [],
    tutorialCategories: [],
    pageVideoHelp: {},
};

defaultSettings.country = resolveActiveCountry(defaultSettings);

/** Deep-Merge: Firebase-Settings behalten Defaults (z. B. Neukunden_Aufschlag), wenn Keys fehlen. */
export const mergeWithDefaultSettings = (incoming: any): SettingsState => {
    const src = incoming && typeof incoming === 'object' ? incoming : {};
    const srcCountries = src.countries && typeof src.countries === 'object' ? src.countries : {};
    const srcOverrides = src.countryOverrides && typeof src.countryOverrides === 'object' ? src.countryOverrides : {};

    const sanitizeAtObj = (obj: any, isOverride = false) => {
        if (!obj || typeof obj !== 'object') return obj;
        const copy = { ...obj };
        if (Number(copy.Lohnnebenkosten) === 30.5) copy.Lohnnebenkosten = 52.0;
        if (Number(copy.Lohnnebenkosten_Minijob) === 30.5 || (!isOverride && Number(copy.Lohnnebenkosten_Minijob) === 52.0)) {
            copy.Lohnnebenkosten_Minijob = 16.4;
        }
        if (Number(copy.Minijob_Lohn_Grenze) === 0) {
            if (isOverride) {
                delete copy.Minijob_Lohn_Grenze;
            } else {
                copy.Minijob_Lohn_Grenze = 518.44;
            }
        }
        return copy;
    };

    const sanitizeChObj = (obj: any) => {
        if (!obj || typeof obj !== 'object') return obj;
        const copy = { ...obj };
        if (Number(copy.Lohnnebenkosten) === 15) copy.Lohnnebenkosten = 17.0;
        if (Number(copy.Lohnnebenkosten_Minijob) === 15) copy.Lohnnebenkosten_Minijob = 17.0;
        return copy;
    };

    const mergedCountries: Record<string, CountryData> = {
        ...defaultSettings.countries,
        ...srcCountries,
        DE: { ...(defaultSettings.countries?.DE || {}), ...(srcCountries.DE || {}) } as CountryData,
        AT: sanitizeAtObj({ ...(defaultSettings.countries?.AT || {}), ...(srcCountries.AT || {}) }, false) as CountryData,
        CH: sanitizeChObj({ ...(defaultSettings.countries?.CH || {}), ...(srcCountries.CH || {}) }) as CountryData,
        IT: { ...(defaultSettings.countries?.IT || {}), ...(srcCountries.IT || {}) } as CountryData,
    };

    const mergedOverrides: Record<string, Partial<CountryData>> = {
        ...(defaultSettings.countryOverrides || {}),
        ...srcOverrides,
    };
    if (mergedOverrides.AT) {
        mergedOverrides.AT = sanitizeAtObj(mergedOverrides.AT, true);
    }
    if (mergedOverrides.CH) {
        mergedOverrides.CH = sanitizeChObj(mergedOverrides.CH);
    }

    const merged: SettingsState = {
        ...defaultSettings,
        ...src,
        global: {
            ...defaultSettings.global,
            ...(src.global || {}),
        },
        webdesignPackages: {
            ...(defaultSettings.webdesignPackages || {}),
            ...(src.webdesignPackages || {}),
        },
        countries: mergedCountries,
        countryOverrides: mergedOverrides,
        country: {
            ...(defaultSettings.country || {}),
            ...(src.country || {}),
        },
        materialListe: Array.isArray(src.materialListe) ? src.materialListe : defaultSettings.materialListe,
        surchargeListe: Array.isArray(src.surchargeListe) ? src.surchargeListe : defaultSettings.surchargeListe,
        priceParameterListe: Array.isArray(src.priceParameterListe)
            ? src.priceParameterListe
            : defaultSettings.priceParameterListe,
        downloadItems: Array.isArray(src.downloadItems) ? src.downloadItems : defaultSettings.downloadItems || [],
        downloadCategories: Array.isArray(src.downloadCategories)
            ? src.downloadCategories
            : defaultSettings.downloadCategories || [],
        tutorialVideos: Array.isArray(src.tutorialVideos) ? src.tutorialVideos : defaultSettings.tutorialVideos || [],
        tutorialCategories: Array.isArray(src.tutorialCategories)
            ? src.tutorialCategories
            : defaultSettings.tutorialCategories || [],
        pageVideoHelp:
            src.pageVideoHelp && typeof src.pageVideoHelp === 'object' && !Array.isArray(src.pageVideoHelp)
                ? { ...src.pageVideoHelp }
                : { ...(defaultSettings.pageVideoHelp || {}) },
    };
    merged.country = resolveActiveCountry(merged);
    return merged;
};

export const defaultAppSettings: AppSettings = { logoUrl: '', basicLogoUrl: '' };

// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// SLICE DEFINITION
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

type SettingsSliceType = Pick<AppState, 
    | 'settings' 
    | 'appSettings' 
    | 'setSettings' 
    | 'updateSettings' 
    | 'updateNestedSettings' 
    | 'updateAppSettings' 
    | 'setInsiderModus' 
    | 'setVatExempt'
>;

// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// SLICE CREATOR
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

export const createSettingsSlice: StateCreator<AppState, [], [], SettingsSliceType> = (set, get) => ({
    settings: defaultSettings,
    appSettings: defaultAppSettings,

    setSettings: (updater) => {
        let newSettings = typeof updater === 'function' ? updater(get().settings) : updater;
        newSettings.country = resolveActiveCountry(newSettings, get().clientData);
        set({ settings: newSettings, isDirty: true }); 
        const { clientData, team, appSettings, savedSnapshots } = get();
        saveToLocalStorage({ clientData, settings: newSettings, team, appSettings, savedSnapshots });
    },
    
    updateSettings: (key, value) => set((state) => { 
        let finalValue = value;

        // Verhindert, dass die UI den Speicher überschreibt (Deep Merge)
        if (key === 'countryOverrides') {
            const currentOverrides = state.settings.countryOverrides || {};
            const newOverrides = value as Record<string, any>;
            
            const mergedOverrides = { ...currentOverrides };
            for (const country in newOverrides) {
                mergedOverrides[country] = {
                    ...(currentOverrides[country] || {}),
                    ...(newOverrides[country] || {})
                };
            }
            finalValue = mergedOverrides;
        }

        const newSettings = { ...state.settings, [key]: finalValue };
        if (key === 'selectedCountryId') {
            newSettings.selectedCountryId = normalizeLandCode(finalValue) as any;
        }
        // Bei Land-Wechsel: selectedCountryId schlägt noch-altes clientData.land
        newSettings.country = resolveActiveCountry(newSettings, state.clientData, {
            preferSettingsLand: key === 'selectedCountryId',
        });
        
        saveToLocalStorage({ clientData: state.clientData, settings: newSettings, team: state.team, appSettings: state.appSettings, savedSnapshots: state.savedSnapshots }); 
        return { settings: newSettings, isDirty: true }; 
    }),
    
    updateNestedSettings: (parentKey, nestedKey, value) => set((state) => { 
        // @ts-ignore
        const parentData = state.settings[parentKey]; 
        if (typeof parentData === 'object' && parentData !== null) { 
            const newParentData = Array.isArray(parentData) 
                ? [...parentData] 
                : { ...parentData, [nestedKey]: value }; 
                
            const newSettings = { ...state.settings, [parentKey]: newParentData }; 
            
            if (parentKey === 'countryOverrides') {
                newSettings.country = resolveActiveCountry(newSettings, state.clientData); 
            }
            
            saveToLocalStorage({ clientData: state.clientData, settings: newSettings, team: state.team, appSettings: state.appSettings, savedSnapshots: state.savedSnapshots }); 
            return { settings: newSettings, isDirty: true }; 
        } 
        return state; 
    }),

    updateAppSettings: (newSettings) => set(state => { 
        const newAppSettings = { ...state.appSettings, ...newSettings }; 
        saveToLocalStorage({ ...state, appSettings: newAppSettings }); 
        return { appSettings: newAppSettings, isDirty: true }; 
    }),

    setInsiderModus: (isInsider: boolean) => set(state => { 
        const newClientData = { 
            ...state.clientData, 
            lizenzDetails: { 
                ...state.clientData.lizenzDetails, 
                typ: isInsider ? ('insider' as 'basic' | 'insider') : ('basic' as 'basic' | 'insider') 
            }, 
            useAlternativeLogo: !isInsider 
        }; 
        saveToLocalStorage({ clientData: newClientData, settings: state.settings, team: state.team, appSettings: state.appSettings, savedSnapshots: state.savedSnapshots }); 
        return { clientData: newClientData, isDirty: true }; 
    }),

    setVatExempt: (isExempt: boolean) => set(state => { 
        const newClientData = { ...state.clientData, isVatExempt: isExempt }; 
        saveToLocalStorage({ ...state, clientData: newClientData }); 
        return { clientData: newClientData, isDirty: true }; 
    })
});